import { useEffect, useState } from 'react';
import { Editor } from '@tiptap/react';

interface EditorState {
    isBold: boolean;
    isItalic: boolean;
    isStrike: boolean;
    isHeading: boolean;
    isBulletList: boolean;
    isOrderedList: boolean;
    isLink: boolean;
}

/**
 * Custom hook to track TipTap editor state changes reactively
 * This ensures toolbar buttons update when cursor moves or selection changes
 */
export const useEditorState = (editor: Editor | null): EditorState => {
    const [editorState, setEditorState] = useState<EditorState>({
        isBold: false,
        isItalic: false,
        isStrike: false,
        isHeading: false,
        isBulletList: false,
        isOrderedList: false,
        isLink: false,
    });

    useEffect(() => {
        if (!editor) {
            return;
        }

        const updateState = () => {
            setEditorState({
                isBold: editor.isActive('bold'),
                isItalic: editor.isActive('italic'),
                isStrike: editor.isActive('strike'),
                isHeading: editor.isActive('heading', { level: 3 }),
                isBulletList: editor.isActive('bulletList'),
                isOrderedList: editor.isActive('orderedList'),
                isLink: editor.isActive('link'),
            });
        };

        // Update state immediately
        updateState();

        // Listen to editor updates (content changes, selection changes, etc.)
        editor.on('update', updateState);
        editor.on('selectionUpdate', updateState);
        editor.on('transaction', updateState);

        // Cleanup listeners on unmount
        return () => {
            editor.off('update', updateState);
            editor.off('selectionUpdate', updateState);
            editor.off('transaction', updateState);
        };
    }, [editor]);

    return editorState;
};
